import React, { useState } from 'react';
import { Shield } from 'lucide-react';
import { FaUserClock, FaBars, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy: React.FC = () => {
    const [consentGiven, setConsentGiven] = useState<boolean>(false);
    const [showConsentModal, setShowConsentModal] = useState<boolean>(true);

    const handleConsent = () => {
        setConsentGiven(true);
        setShowConsentModal(false);
    };

    const currentYear = new Date().getFullYear();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-white text-gray-800">
            <nav className="fixed w-full top-0 left-0 bg-white/90 backdrop-blur-md z-50 shadow-sm">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">

                        {/* Logo */}
                        <div
                            className="flex items-center space-x-2 cursor-pointer"
                            onClick={() => navigate("/")}
                        >
                            <FaUserClock className="text-blue-600 text-2xl" />
                            <span className="text-xl font-bold text-gray-900">
                                HRPayroll
                            </span>
                        </div>

                        {/* Desktop Nav */}
                        <div className="hidden md:flex items-center space-x-8">
                            <button
                                onClick={() => navigate("/")}
                                className="text-gray-700 hover:text-blue-600 transition"
                            >
                                Home
                            </button>

                            <button
                                onClick={() => navigate("/#features")}
                                className="text-gray-700 hover:text-blue-600 transition"
                            >
                                Features
                            </button>

                            <button
                                onClick={() => navigate("/#pricing")}
                                className="text-gray-700 hover:text-blue-600 transition"
                            >
                                Pricing
                            </button>

                            <button
                                onClick={() => navigate("/")}
                                className="text-gray-700 hover:text-blue-600 transition"
                            >
                                Login
                            </button>
                        </div>

                        {/* Mobile menu */}
                        <button
                            className="md:hidden text-gray-700"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                        >
                            {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                        </button>
                    </div>

                    {/* Mobile Nav */}
                    {isMenuOpen && (
                        <div className="md:hidden mt-4 pb-4">
                            <div className="flex flex-col space-y-4">
                                <button onClick={() => navigate("/")}>Home</button>
                                <button onClick={() => navigate("/#features")}>Features</button>
                                <button onClick={() => navigate("/#pricing")}>Pricing</button>
                                <button onClick={() => navigate("/")}>Login</button>
                            </div>
                        </div>
                    )}
                </div>
            </nav>
            {/* Consent Modal */}
            {showConsentModal && !consentGiven && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
                        <div className="flex items-center gap-3 mb-4">
                            <Shield className="w-6 h-6 text-indigo-600" />
                            <h3 className="text-xl font-bold text-gray-900">Acknowledge Privacy Policy</h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-6">
                            By using Teamlio, you agree to our data collection practices. You have the right to withdraw consent at any time.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={handleConsent}
                                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 rounded-lg transition"
                            >
                                I Agree
                            </button>
                            <button
                                onClick={() => window.location.href = '/'}
                                className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 rounded-lg transition"
                            >
                                Decline
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="w-full px-6 md:px-12 lg:px-24 xl:px-32 py-12 mt-16">
                {/* Header */}
                <div className="w-full flex justify-center mb-12">
                    <div className="text-center max-w-4xl">
                        <p className="text-gray-500 text-base mb-2">
                            Last updated: 15th March 2026
                        </p>
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                            Privacy Policy – Teamlio
                        </h1>
                        <p className="text-gray-600 text-lg mt-4 leading-relaxed">
                            This Privacy Policy explains how Teamlio collects, uses, and protects user data. 
                            By using our platform and browser extension, you agree to the practices described below.
                        </p>
                    </div>
                </div>

                {/* User Consent */}
                <div className="mb-12">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. User Consent</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-3">
                        We collect data only after obtaining user consent. The Teamlio browser extension is installed and activated only with the knowledge and permission of the user or organization. This ensures complete transparency in our data collection practices.
                    </p>
                    <p className="text-gray-600 text-lg leading-relaxed">
                        You have the right to withdraw your consent at any time. Withdrawal of consent will immediately stop all data collection activities from your browser. However, previously collected data may be retained as per organizational policies.
                    </p>
                    {consentGiven && (
                        <div className="mt-4 flex items-center gap-3">
                            <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">✓ Consent Active</span>
                            <button
                                onClick={() => setShowConsentModal(true)}
                                className="text-sm text-red-500 hover:underline"
                            >
                                Withdraw Consent
                            </button>
                        </div>
                    )}
                </div>

                {/* Data We Collect */}
                <div className="mb-12">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Data We Collect</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-4">
                        Teamlio collects only the minimum necessary data to provide productivity insights. All data collection is transparent and purpose-driven:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 text-lg space-y-3">
                        <li><strong className="text-gray-800">Active browser window URLs during working hours</strong> - We track the URLs of websites visited during configured work hours to understand work patterns and identify productive vs. non-productive activities.</li>
                        <li><strong className="text-gray-800">Activity data for productivity tracking</strong> - This includes timestamps of active usage, idle time detection, and application switching patterns to measure focus and efficiency.</li>
                        <li><strong className="text-gray-800">Basic usage metadata for system improvement</strong> - Anonymous usage statistics help us improve Teamlio's performance, fix bugs, and develop new features that benefit all users.</li>
                    </ul>
                    <p className="text-gray-600 text-lg leading-relaxed mt-4">
                        We do NOT collect passwords, financial information, personal messages, or any sensitive personal data. Your privacy is our priority.
                    </p>
                </div>

                {/* Browser Extension Monitoring */}
                <div className="mb-12">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Browser Extension Monitoring</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-3">
                        The Teamlio extension monitors active browser activity to help organizations understand work patterns and productivity during office hours. The extension captures:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 text-lg space-y-2 mb-3">
                        <li>Active tab URLs (including incognito sessions if enabled by organization settings)</li>
                        <li>Duration spent on each website or web application</li>
                        <li>Switching between tabs and applications</li>
                        <li>Categorization of websites (productive, neutral, non-productive)</li>
                    </ul>
                    <p className="text-gray-600 text-lg leading-relaxed">
                        This monitoring helps identify non-work-related browsing during work hours and enables organizations to optimize resource allocation. All data is encrypted and accessible only to authorized administrators.
                    </p>
                </div>

                {/* Email Activity Monitoring */}
                <div className="mb-12">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Email Activity Monitoring</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-3">
                        Teamlio may capture limited metadata related to email usage for productivity insights. Specifically, we track:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 text-lg space-y-2 mb-3">
                        <li>Recipient email addresses (to whom emails are sent) - only domain level for analytics</li>
                        <li>Email send and receive timestamps</li>
                        <li>Email thread participation counts</li>
                        <li>Response time metrics</li>
                    </ul>
                    <p className="text-gray-600 text-lg leading-relaxed">
                        <strong>IMPORTANT:</strong> Teamlio does NOT access, read, store, or share email content, subject lines, or attachments. Your email content remains completely private and confidential to you and your organization.
                    </p>
                </div>

                {/* Purpose of Data Collection */}
                <div className="mb-12">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Purpose of Data Collection</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-4">
                        The data collected by Teamlio serves several important business purposes that benefit both organizations and employees:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 text-lg space-y-3">
                        <li><strong className="text-gray-800">Employee productivity tracking</strong> - Measure individual and team productivity to identify high performers and those who may need additional support or training.</li>
                        <li><strong className="text-gray-800">Work-hour activity monitoring</strong> - Ensure that work hours are being utilized effectively and identify patterns of overtime or underutilization.</li>
                        <li><strong className="text-gray-800">Prevent misuse of company time/resources</strong> - Detect excessive non-work-related activities during paid work hours to protect company resources.</li>
                        <li><strong className="text-gray-800">Performance optimization</strong> - Identify bottlenecks, inefficient processes, and areas where automation could improve workflow.</li>
                        <li><strong className="text-gray-800">Compliance and security</strong> - Ensure adherence to company policies and detect potential security threats or data breaches.</li>
                    </ul>
                </div>

                {/* Data Protection */}
                <div className="mb-12">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Protection</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-3">
                        We take data protection seriously and implement industry-standard security measures:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 text-lg space-y-2 mb-3">
                        <li><strong className="text-gray-800">Encryption</strong> - All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption.</li>
                        <li><strong className="text-gray-800">Access Control</strong> - Strict role-based access control ensures only authorized personnel can view sensitive data.</li>
                        <li><strong className="text-gray-800">Audit Logging</strong> - Complete audit trails of who accessed what data and when.</li>
                        <li><strong className="text-gray-800">Regular Security Audits</strong> - Third-party security assessments are conducted regularly.</li>
                        <li><strong className="text-gray-800">Data Minimization</strong> - We collect only the data necessary for the stated purposes.</li>
                    </ul>
                    <p className="text-gray-600 text-lg leading-relaxed">
                        Access to collected data is restricted to organizational administrators and is used strictly for legitimate business purposes. We never sell or share your data with third parties for marketing purposes.
                    </p>
                </div>

                {/* Transparency & Responsibility */}
                <div className="mb-12">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Transparency & Responsibility</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-3">
                        Organizations using Teamlio bear significant responsibility in maintaining transparency with their employees:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 text-lg space-y-2 mb-3">
                        <li>Informing employees about monitoring practices before implementation</li>
                        <li>Obtaining proper consent as required by applicable laws</li>
                        <li>Ensuring compliance with local, state, and national privacy regulations</li>
                        <li>Providing employees access to their own data upon request</li>
                        <li>Establishing clear policies regarding acceptable use of company resources</li>
                    </ul>
                    <p className="text-gray-600 text-lg leading-relaxed">
                        Teamlio provides tools and documentation to help organizations maintain compliance with regulations such as GDPR, CCPA, and local labor laws.
                    </p>
                </div>

                {/* Updates to Policy */}
                <div className="mb-12">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Updates to Policy</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-3">
                        We may update this privacy policy from time to time to reflect changes in our practices, technology, legal requirements, or regulatory guidelines. When we make material changes:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 text-lg space-y-2 mb-4">
                        <li>We will notify you via email at least 30 days in advance of significant changes</li>
                        <li>A notice will be displayed within the Teamlio application</li>
                        <li>The "Last updated" date at the top of this policy will be revised</li>
                        <li>Continued use of Teamlio after changes indicates acceptance of updated terms</li>
                    </ul>
                    <p className="text-gray-600 text-lg leading-relaxed mb-4">
                        If you do not agree with any changes to this policy, you may withdraw consent and discontinue use of Teamlio. You may also request deletion of your data as outlined in your data rights.
                    </p>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg flex justify-between items-center flex-wrap gap-3">
                        <span className="text-base text-gray-500">Current Version: v2.0 | Effective: March 15, 2026</span>
                        <button
                            onClick={() => alert('Acknowledgment recorded')}
                            className="text-base bg-indigo-50 text-indigo-600 px-4 py-2 rounded-full hover:bg-indigo-100 transition"
                        >
                            Acknowledge Latest
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-12 pt-6 border-t border-gray-200 text-center text-base text-gray-500">
                    <p>© {currentYear} Teamlio. All rights reserved.</p>
                    <div className="flex justify-center gap-6 mt-3">
                        <a href="#" className="hover:text-indigo-600">Contact Us</a>
                        <a href="#" className="hover:text-indigo-600">Report an Issue</a>
                        <a href="#" className="hover:text-indigo-600">Data Request</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;